<template>
  <div class="node" :class="{ selected: currentOpenedNode === node.id }" @click="handleClick">
    <div class="content">
      <header class="margin-bottom">
        <h2 :class="{ 'option-label': isOptionNode }">
          <template v-if="node.type === NodeType.NumericDialing">
            <NumberOutlined class="node__icon" />
            {{ node.name_numerical_dialing || "Marcaciones numéricas" }}
            <span class="identifier">(MN{{ node.id }})</span>
          </template>

          <template v-if="node.type === NodeType.VariableCapture">
            <SaveOutlined class="node__icon" />
            Capturar variable <span class="identifier">(CV{{ node.id }})</span>
          </template>

          <template v-if="node.type === NodeType.SatisfactionRanking">
            <StarOutlined class="node__icon" />
            Ranking de satisfacción <span class="identifier">(RS{{ node.id }})</span>
          </template>

          <template v-if="node.type === NodeType.Option">
            {{ node.order }}. Opción {{ node.order }}
          </template>

          <template v-if="node.type === NodeType.OptionConsultApi">
            {{ node.order }}. {{ node.referencial_name }}
            <!-- {{ (node.option == OptionConnectorApi ? 'Document - ' : '') }} Opción {{ node.order }} -->
          </template>
          
          <template v-if="node.type === NodeType.WrongOption"> Opción incorrecta </template>

          <template v-if="node.type === NodeType.WrongOptionApi"> Opción incorrecta de API </template>

          <template v-if="node.type === NodeType.ExpiredTime"> Tiempo expirado </template>
        </h2>

        <p
          class="actions__detail margin-bottom"
          v-if="node.action && node.type !== NodeType.NumericDialing"
        >
          <InfoCircleOutlined v-if="node.action === ActionType.EndCall" />
          {{ nodeActionDetail }}
        </p>
      </header>

      <div class="body">
        <p class="description" :title="hasAudioFile ? getFileName(node.file_audio_url) : ''">
          <template v-if="hasAudioFile">
            <div class="flex-center">
              audio:
              <p v-if="node.file_audio_url">{{ getFileName(node.file_audio_url) }}</p>
              <p v-else class="required-ivr-audio">
                <WarningOutlined /> Sin audio. Campo requerido.
              </p>
            </div>
          </template>

          <template v-if="node.type == NodeType.OptionConsultApi">
            {{ TextOptionConnectorApi }}
          </template>

          <template v-if="node.type === NodeType.Option">
            {{ node.option || "-" }}
          </template>

          <template v-if="node.type === NodeType.WrongOption">
            El contacto marca cualquier otro dígito no configurado
          </template>

          <template v-if="node.type === NodeType.WrongOptionApi">
            Error en respuesta de API
          </template>

          <template v-if="node.type === NodeType.ExpiredTime">
            Límite de tiempo para esperar una respuesta por el contacto.
          </template>
        </p>

        <div class="actions__button">
          <template v-if="node.type === NodeType.NumericDialing">
            <a-button
              type="link"
              class="button"
              :disabled="botDetails?.status === BotStatus.Active"
              @click.stop="handleAddOption"
            >
              Agregar opción
            </a-button>

            <a-divider type="vertical" class="divider" />
          </template>

          <a-button
            v-if="showDeleteButton"
            type="link"
            danger
            class="button"
            :disabled="botDetails?.status === BotStatus.Active"
            @click.stop="handleDelete"
          >
            Eliminar
          </a-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  SaveOutlined,
  NumberOutlined,
  StarOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons-vue";
import { computed, inject, nextTick } from "vue";

import { Node } from "@/@types/ivr";
import { ActionType, BotStatus, NodeType, TextOptionConnectorApi } from "@/app/ivr/bot/utils/enums";
import { ActionText, numericalOptions } from "@/app/ivr/bot/utils/texts";
import { BotDetailsKey, CurrentOpenedNodeKey, FetchNodesKey } from "@/@types/ivr/symbols";
import { nodeServices } from "@/app/ivr/bot/services/NodeServices";
import { ResponseError } from "@/app/shared/utils/httpManager";
import { message } from "ant-design-vue";

const props = defineProps<{
  node: Node;
  parentNode: Node | undefined;
}>();

const emit = defineEmits<{
  (e: "click", id: number): void;
  (e: "delete", id: number[]): void;
}>();

const botDetails = inject(BotDetailsKey);
const currentOpenedNode = inject(CurrentOpenedNodeKey);
const fetchNodes = inject(FetchNodesKey);

const hasAudioFile = computed(() =>
  [NodeType.NumericDialing, NodeType.VariableCapture, NodeType.SatisfactionRanking].includes(
    props.node.type as NodeType
  )
);

const showDeleteButton = computed(() => {
  if (props.node.type === NodeType.Option) return props.node.order > 1;

  if (props.node.type === NodeType.OptionConsultApi) return false;
  
  return ![NodeType.WrongOption, NodeType.WrongOptionApi, NodeType.ExpiredTime].includes(props.node.type as NodeType);
});

const isOptionNode = computed(() =>
  [NodeType.Option, NodeType.WrongOption, NodeType.WrongOptionApi, NodeType.ExpiredTime].includes(props.node.type as NodeType)
);

// SET ACTION DETAIL IN NODE
const nodeActionDetail = computed(() => {
  if (props.node.action === ActionType.BackToNode) {
    if ([NodeType.WrongOption, NodeType.WrongOptionApi, NodeType.ExpiredTime].includes(props.node.type as NodeType)) {
      if (props.node.parent_type === NodeType.VariableCapture) {
        return ActionText[props.node.action][2];
      }
      return ActionText[props.node.action][1];
    } else {
      return ActionText[props.node.action][0];
    }
  } else if (props.node?.action) {
    return ActionText[props.node.action];
  }
  return "";
});

const handleClick = () => emit("click", props.node.id);

const getFileName = (url: string | null) => {
  if (!url) return "";

  const decodedUrl = decodeURIComponent(url);

  return decodedUrl.split("/").at(-1)?.replaceAll(":", "_");
};

const handleAddOption = async () => {
  try {
    if (props.node.child.length >= 15) return message.error("Solo pueden existir 13 opciones");

    const clone = JSON.parse(JSON.stringify(props.node));
    const newNode = structuredClone(clone);

    const takenOptions: string[] = props.node.child.reduce((acc: string[], cur: Node) => {
      if (cur?.option) acc.push(cur.option);
      return acc;
    }, []);

    let nextAvailable = newNode.option;

    for (const currentOption of takenOptions) {
      const index = numericalOptions.indexOf(currentOption); // indice del elemento recorrido.
      const possibleOption = numericalOptions[index + 1]; // Cual es la siguiente?

      if (!takenOptions.includes(possibleOption)) {
        // ya esta tomada?
        nextAvailable = possibleOption; // la asignamos a la proxima
        break; // detenemos el bucle
      }
    }

    newNode.option = nextAvailable;

    await nodeServices.create(props.node.ivr, NodeType.Option, newNode);
  } catch (error) {
    const errorMessage =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.error(errorMessage);
  }

  fetchNodes?.();
};

const handleDelete = async () => {
  try {
    await nodeServices.delete(props.node, props.parentNode);

    emit("delete", [props.node.id, ...props.node.child.map((c) => c.id)]);
  } catch (error) {
    const errorMessage =
      error instanceof ResponseError ? error.getErrorMessage() : "Ha ocurrido un error";

    message.error(errorMessage);
  }

  await nextTick();

  fetchNodes?.();
};
</script>

<style scoped lang="sass">
h2, p
  margin: 0

.node
  border: 1px solid $gray-5
  border-radius: 4px
  padding: 12px
  width: 460px
  margin-bottom: 8px
  cursor: pointer

  &:hover
    border-color: $blue-6

  &.selected
    border-color: $blue-6

  &__icon
    color: $blue-6
    font-weight: 600
    font-size: 18px
    margin-right: 10px

h2
  color: $gray-9
  font-weight: 600
  font-size: 14px
  display: flex
  align-items: center
  line-height: 22px

  &.option-label
    color: $gray-8
    font-size: 12px

  .identifier
    font-size: 12px
    font-weight: 400
    color: $gray-7
    margin-left: 4px

header
  display: flex
  justify-content: space-between
  align-items: center
  margin-bottom: 4px

.body
  color: $gray_dark_600
  display: flex
  justify-content: space-between
  align-items: center

  .description
    overflow: hidden
    white-space: nowrap
    text-overflow: ellipsis

.button
  padding: 0
  height: 22px

.divider
  background-color: $gray-6

.actions
  &__detail
    color: $gray-7
    text-align: right
    font-style: italic

  &__button
    text-align: right
    flex: 0 0 auto

.flex-center
  display: flex
  align-items: center
  gap: 5px

.required-ivr-audio
  color: $gold-6
</style>
